class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.rangeMin = this.querySelector('input[type="range"]:first-child');
    this.rangeMax = this.querySelector('input[type="range"]:last-child');
    this.inputMin = this.querySelector('input[name="filter.v.price.gte"]');
    this.inputMax = this.querySelector('input[name="filter.v.price.lte"]');
    this.inputCommitDelay = 2000;
    this.inputCommitTimer = null;

    if (!this.rangeMin || !this.rangeMax || !this.inputMin || !this.inputMax) return;

    this.inputMin.addEventListener("focus", this.inputMin.select);
    this.inputMax.addEventListener("focus", this.inputMax.select);
    this.inputMin.addEventListener("input", this.onInputTyping.bind(this));
    this.inputMax.addEventListener("input", this.onInputTyping.bind(this));
    this.inputMin.addEventListener("change", this.onInputMinChange.bind(this));
    this.inputMax.addEventListener("change", this.onInputMaxChange.bind(this));

    this.rangeMin.addEventListener("change", this.onRangeMinChange.bind(this));
    this.rangeMax.addEventListener("change", this.onRangeMaxChange.bind(this));
    this.rangeMin.addEventListener("input", this.onRangeMinInput.bind(this));
    this.rangeMax.addEventListener("input", this.onRangeMaxInput.bind(this));
  }

  dispatchFilterInput() {
    this.dispatchEvent(new Event("input", { bubbles: true }));
  }

  clearInputCommitTimer() {
    if (!this.inputCommitTimer) return;

    window.clearTimeout(this.inputCommitTimer);
    this.inputCommitTimer = null;
  }

  scheduleInputCommit(input) {
    this.clearInputCommitTimer();
    this.inputCommitTimer = window.setTimeout(() => {
      this.commitInput(input);
    }, this.inputCommitDelay);
  }

  commitInput(input) {
    this.clearInputCommitTimer();

    if (input === this.inputMin) {
      this.normalizeInputMin(input);
    } else {
      this.normalizeInputMax(input);
    }

    this.dispatchFilterInput();
  }

  getNumber(value, fallbackValue) {
    const parsedValue = parseInt(value, 10);
    return Number.isFinite(parsedValue) ? parsedValue : fallbackValue;
  }

  updateRangeProgress(bound, value) {
    const range = bound === "min" ? this.rangeMin : this.rangeMax;
    const property = bound === "min" ? "--from" : "--to";
    const max = this.getNumber(range.max, 1) || 1;

    range.parentElement.style.setProperty(property, `${(this.getNumber(value, 0) / max) * 100}%`);
  }

  onInputTyping(event) {
    event.stopPropagation();
    this.scheduleInputCommit(event.target);
  }

  normalizeInputMin(input) {
    const minValue = this.getNumber(input.min, 0);
    const maxValue = this.getNumber(this.inputMax.value, this.getNumber(input.max, minValue + 1));
    const inputValue = this.getNumber(input.value, NaN);

    if (!Number.isFinite(inputValue)) {
      input.value = "";
      this.rangeMin.value = minValue;
      this.updateRangeProgress("min", minValue);
      return;
    }

    input.value = Math.max(Math.min(inputValue, maxValue - 1), minValue);
    this.rangeMin.value = input.value;
    this.updateRangeProgress("min", this.rangeMin.value);
  }

  normalizeInputMax(input) {
    const maxValue = this.getNumber(input.max, 0);
    const minValue = this.getNumber(this.inputMin.value, this.getNumber(input.min, 0));
    const inputValue = this.getNumber(input.value, NaN);

    if (!Number.isFinite(inputValue)) {
      input.value = "";
      this.rangeMax.value = maxValue;
      this.updateRangeProgress("max", maxValue);
      return;
    }

    input.value = Math.min(Math.max(inputValue, minValue + 1), maxValue);
    this.rangeMax.value = input.value;
    this.updateRangeProgress("max", this.rangeMax.value);
  }

  onInputMinChange(event) {
    event.preventDefault();
    event.stopPropagation();
    this.commitInput(event.target);
  }

  onInputMaxChange(event) {
    event.preventDefault();
    event.stopPropagation();
    this.commitInput(event.target);
  }

  onRangeMinChange(event) {
    event.stopPropagation();
    this.dispatchFilterInput();
  }

  onRangeMaxChange(event) {
    event.stopPropagation();
    this.dispatchFilterInput();
  }

  onRangeMinInput(event) {
    event.stopPropagation();
    event.target.value = Math.min(
      this.getNumber(event.target.value, 0),
      this.getNumber(this.inputMax.value, this.getNumber(event.target.max, 1)) - 1
    );
    this.updateRangeProgress("min", event.target.value);
    this.inputMin.value = event.target.value;
  }

  onRangeMaxInput(event) {
    event.stopPropagation();
    event.target.value = Math.max(
      this.getNumber(event.target.value, 0),
      this.getNumber(this.inputMin.value, this.getNumber(event.target.min, 0)) + 1
    );
    this.updateRangeProgress("max", event.target.value);
    this.inputMax.value = event.target.value;
  }
}
if (!customElements.get("price-range")) {
  customElements.define("price-range", PriceRange);
}
